import { 
  Directive, 
  Input, 
  TemplateRef, 
  ViewContainerRef, 
  OnInit, 
  OnDestroy 
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { UserContextService } from '../../services/common/user-context.service';
import { AccessType } from '../../models/authorization';

/**
 * Structural directive to conditionally show/hide elements based on user permission access
 * 
 * Usage examples:
 * - Check permission with access level: <button *hasAccess="'Administration.Users.Page':'Write'">Edit</button>
 * - Check read access: <div *hasAccess="'Dashboard.Main.Page':'Read'">View Dashboard</div>
 */
@Directive({
  selector: '[hasAccess]'
})
export class AccessDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private permissionKey: string = '';
  private requiredAccess: AccessType = AccessType.Read;
  private hasView = false;

  @Input() 
  set hasAccess(value: string) {
    this.parseAccessExpression(value);
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private userContextService: UserContextService
  ) {}

  ngOnInit() {
    // React to context changes
    this.userContextService.userContext$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Parse access expression in format "permissionKey:access"
   */
  private parseAccessExpression(value: string): void {
    const [key, access] = value.split(':');
    
    if (!key || !access) {
      console.warn('[AccessDirective] Invalid format. Expected "permissionKey:access"', value);
      return;
    }

    this.permissionKey = key.trim();
    this.requiredAccess = access.trim() as AccessType;
  }

  /**
   * Update view visibility based on current access
   */
  private updateView(): void {
    const hasAccess = this.checkAccess();

    if (hasAccess && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasAccess && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  /**
   * Check if user has required access
   */
  private checkAccess(): boolean {
    if (!this.permissionKey || !this.requiredAccess) {
      console.warn('[AccessDirective] No valid permission check configured');
      return false;
    }

    return this.userContextService.hasAccess(
      this.permissionKey, 
      this.requiredAccess
    );
  }
}
